import {
  Checkbox,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@sandtech-hub-monorepo/ui-kit';
import { PhoneInput } from 'react-international-phone';
import {
  Control,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
  Path,
  PathValue,
} from 'react-hook-form';

interface PhoneNumberFormFields extends FieldValues {
  phoneNumber: string;
  whatsAppNumber: string;
  sameAsPrimaryPhone: boolean;
}

interface PhoneNumberFieldsProps<
  T extends PhoneNumberFormFields = PhoneNumberFormFields
> {
  control: Control<T>;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
}

const PHONE_INPUT_STYLES = {
  inputClassName:
    '!flex-1 !w-full !h-10 !px-3 !py-2 !border !border-border !rounded-r-lg !text-sm !text-foreground !bg-background',
  countrySelectorStyleProps: {
    buttonClassName:
      '!h-10 !border-border !bg-background !hover:bg-background !px-2 !rounded-l-lg',
    dropdownStyleProps: {
      className: '!bg-card !max-w-40 !sm:max-w-52 max-h-40',
      listItemClassName:
        '!hover:bg-card !bg-card !text-foreground !py-1 !ml-1',
    },
  },
} as const;

export const PhoneNumberFields = <
  T extends PhoneNumberFormFields = PhoneNumberFormFields
>({
  control,
  watch,
  setValue,
}: PhoneNumberFieldsProps<T>) => {
  const phoneNumberPath = 'phoneNumber' as Path<T>;
  const whatsAppNumberPath = 'whatsAppNumber' as Path<T>;
  const sameAsPrimaryPhonePath = 'sameAsPrimaryPhone' as Path<T>;

  const syncWhatsAppNumber = (phone: string) => {
    setValue(whatsAppNumberPath, phone as PathValue<T, Path<T>>);
  };

  return (
    <>
      <FormField
        control={control}
        name={phoneNumberPath}
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel
              htmlFor="phoneNumber"
              className="text-sm !text-foreground"
            >
              Phone Number*
            </FormLabel>
            <FormControl>
              <PhoneInput
                defaultCountry="af"
                {...PHONE_INPUT_STYLES}
                {...field}
                onChange={(phone) => {
                  field.onChange(phone);
                  if (watch(sameAsPrimaryPhonePath)) {
                    syncWhatsAppNumber(phone);
                  }
                }}
              />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={whatsAppNumberPath}
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel
              htmlFor="whatsAppNumber"
              className="text-sm !text-foreground"
            >
              WhatsApp Number*
            </FormLabel>
            <FormControl>
              <PhoneInput
                disabled={watch(sameAsPrimaryPhonePath) as boolean}
                defaultCountry="af"
                {...PHONE_INPUT_STYLES}
                {...field}
              />
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={sameAsPrimaryPhonePath}
        render={({ field }) => (
          <FormItem className="flex gap-2 items-start space-y-0">
            <Checkbox
              id="sameAsPrimaryPhone"
              className="mt-0.5 border-border"
              checked={field.value}
              onCheckedChange={(checked) => {
                if (checked) {
                  const phoneNumber = watch(phoneNumberPath) as PathValue<
                    T,
                    Path<T>
                  >;
                  setValue(whatsAppNumberPath, phoneNumber);
                }
                field.onChange(checked);
              }}
            />
            <div className="flex flex-col gap-1">
              <FormLabel htmlFor="sameAsPrimaryPhone" className="text-sm">
                The same as primary phone number
              </FormLabel>
              <FormDescription className="text-xs">
                ALX will use this number for ease of communication with you.
              </FormDescription>
            </div>
          </FormItem>
        )}
      />
    </>
  );
};
